import { useEffect, useMemo, useRef, useState } from "react";
import * as Icons from "lucide-react";

/* Structured multi-service + multi-year intake editor (Phase 2).
   Writes canonical requested_services[] via onChange. Year controls store explicit
   integers only; shortcuts/ranges are interaction helpers, not stored values. */

const YA = { specific_years: "Specific years", not_applicable: "Not year-specific", unknown: "Unknown" };

let _seq = 0;
const nextKey = () => `rs-${Date.now()}-${_seq++}`;

const spanLabel = (years) => {
  const ys = [...new Set(years)].sort((a, b) => a - b);
  if (!ys.length) return "";
  const parts = [];
  let start = ys[0], prev = ys[0];
  for (const y of ys.slice(1)) {
    if (y === prev + 1) { prev = y; continue; }
    parts.push(start === prev ? `${start}` : `${start}\u2013${prev}`);
    start = prev = y;
  }
  parts.push(start === prev ? `${start}` : `${start}\u2013${prev}`);
  return parts.join(", ");
};

const itemSummary = (r) => {
  const bits = [r.service_name || "Service"];
  if (r.category_name) bits.push(r.category_name);
  if (r.year_applicability === "specific_years" && r.years?.length) bits.push(spanLabel(r.years));
  else if (r.year_applicability === "not_applicable") bits.push("Not year-specific");
  else if (r.year_applicability === "unknown") bits.push("Year TBD");
  return bits.join(" \u00b7 ");
};

const sig = (items) => JSON.stringify((items || []).map((i) => [i.service_code, i.service_id, i.service_name,
  i.year_applicability, i.years, i.custom_service_description]));

function YearControl({ row, bounds, onYears, onApplicability }) {
  const [from, setFrom] = useState("");
  const [thru, setThru] = useState("");
  const [older, setOlder] = useState("");
  const max = bounds?.max || new Date().getFullYear();
  const min = bounds?.min || 1990;
  const selected = row.years || [];
  const windowYears = useMemo(() => {
    const arr = [];
    for (let y = max; y >= max - 7; y--) arr.push(y);
    for (const y of selected) if (!arr.includes(y)) arr.push(y);
    return [...new Set(arr)].sort((a, b) => b - a);
  }, [max, selected.join(",")]);

  const setYears = (ys) => onYears([...new Set(ys)].filter((y) => y >= min && y <= max).sort((a, b) => a - b));
  const toggle = (y) => selected.includes(y) ? setYears(selected.filter((x) => x !== y)) : setYears([...selected, y]);
  const applyRange = () => {
    const a = parseInt(from, 10), b = parseInt(thru, 10);
    if (!a || !b) return;
    const lo = Math.min(a, b), hi = Math.max(a, b);
    const r = []; for (let y = lo; y <= hi; y++) r.push(y);
    setYears([...selected, ...r]); setFrom(""); setThru("");
  };
  const addOlder = () => { const y = parseInt(older, 10); if (y) { setYears([...selected, y]); setOlder(""); } };
  const shortcut = (n) => setYears(Array.from({ length: n }, (_, i) => max - i));

  return (
    <div className="space-y-2" data-testid="rs-year-control">
      <div className="flex flex-wrap gap-1.5">
        {Object.entries(YA).map(([k, lbl]) => (
          <button key={k} type="button" onClick={() => onApplicability(k)}
            data-testid={`rs-ya-${k}`}
            className={`text-xs font-medium rounded-full px-2.5 py-1 border transition-colors ${row.year_applicability === k ? "bg-blue-600 text-white border-blue-600" : "text-slate-600 border-slate-300 hover:bg-slate-50"}`}>
            {lbl}
          </button>
        ))}
      </div>
      {row.year_applicability === "specific_years" && (
        <div className="space-y-2 rounded-lg bg-slate-50 border border-slate-100 p-2.5">
          <div className="flex flex-wrap gap-1.5" data-testid="rs-year-chips">
            {windowYears.map((y) => (
              <button key={y} type="button" onClick={() => toggle(y)} data-testid={`rs-year-${y}`}
                aria-pressed={selected.includes(y)}
                className={`text-xs font-semibold rounded-md px-2 py-1 border transition-colors ${selected.includes(y) ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200 hover:border-blue-300"}`}>
                {y}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
            <span className="text-slate-400">Shortcuts:</span>
            <button type="button" onClick={() => shortcut(1)} data-testid="rs-sc-current" className="rounded-md px-2 py-0.5 border border-slate-200 hover:bg-white">Current year</button>
            <button type="button" onClick={() => shortcut(3)} data-testid="rs-sc-3" className="rounded-md px-2 py-0.5 border border-slate-200 hover:bg-white">Last 3 years</button>
            <button type="button" onClick={() => shortcut(6)} data-testid="rs-sc-6" className="rounded-md px-2 py-0.5 border border-slate-200 hover:bg-white">Last 6 years</button>
            <button type="button" onClick={() => setYears([])} data-testid="rs-sc-clear" className="rounded-md px-2 py-0.5 border border-slate-200 hover:bg-white text-rose-500">Clear</button>
          </div>
          <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
            <span className="text-slate-400">Range:</span>
            <input value={from} onChange={(e) => setFrom(e.target.value)} placeholder="From" data-testid="rs-range-from"
              className="w-16 rounded-md border border-slate-200 px-1.5 py-0.5" inputMode="numeric" aria-label="Range from year" />
            <input value={thru} onChange={(e) => setThru(e.target.value)} placeholder="Through" data-testid="rs-range-thru"
              className="w-16 rounded-md border border-slate-200 px-1.5 py-0.5" inputMode="numeric" aria-label="Range through year" />
            <button type="button" onClick={applyRange} data-testid="rs-range-apply" className="rounded-md px-2 py-0.5 bg-slate-700 text-white hover:bg-slate-800">Apply</button>
            <span className="text-slate-300">|</span>
            <input value={older} onChange={(e) => setOlder(e.target.value)} placeholder="Add year" data-testid="rs-add-year"
              className="w-20 rounded-md border border-slate-200 px-1.5 py-0.5" inputMode="numeric" aria-label="Add a specific year" />
            <button type="button" onClick={addOlder} data-testid="rs-add-year-btn" className="rounded-md px-2 py-0.5 border border-slate-200 hover:bg-white">Add</button>
          </div>
          {selected.length > 0 && <div className="text-[11px] text-slate-500" data-testid="rs-year-summary">Selected: {spanLabel(selected)}</div>}
        </div>
      )}
    </div>
  );
}

function ServiceRow({ row, idx, options, priorRow, review, onChange, onRemove }) {
  const [collapsed, setCollapsed] = useState(false);
  const [askApply, setAskApply] = useState(false);
  const isOther = row.service_code === options.other_code;

  const selectService = (val) => {
    if (!val) { onChange({ ...row, service_id: null, service_code: "", service_name: "", category_code: null, category_name: null }); return; }
    if (val === options.other_code) {
      onChange({ ...row, service_id: null, service_code: options.other_code, service_name: "Other", category_code: null, category_name: null, year_applicability: "unknown", years: [] });
      return;
    }
    const svc = options.services.find((s) => s.service_code === val);
    if (!svc) return;
    onChange({ ...row, service_id: svc.service_id, service_code: svc.service_code, service_name: svc.service_name,
      category_code: svc.category_code, category_name: svc.category_name,
      year_applicability: row.year_applicability || svc.year_applicability_default || "specific_years" });
    // offer to copy prior service's years (never silent)
    if (idx > 0 && priorRow?.year_applicability === "specific_years" && priorRow?.years?.length) setAskApply(true);
  };

  const grouped = useMemo(() => {
    const g = {};
    for (const s of options.services) { (g[s.category_name || "Other"] ||= []).push(s); }
    return Object.entries(g).sort((a, b) => a[0].localeCompare(b[0]));
  }, [options.services]);

  return (
    <div className={`rounded-xl border bg-white ${review ? "border-amber-300 ring-1 ring-amber-100" : "border-slate-200"}`} data-testid={`rs-row-${idx}`}>
      <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100">
        <span className="h-5 w-5 rounded-full bg-blue-50 text-blue-600 text-[10px] font-bold flex items-center justify-center">{idx + 1}</span>
        <span className="text-xs font-semibold text-slate-500">Requested Service {idx + 1}</span>
        {review && <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 rounded-full px-2 py-0.5" data-testid={`rs-row-review-${idx}`}>Needs specific selection</span>}
        {collapsed && row.service_name && <span className="text-xs text-slate-500 truncate" data-testid={`rs-row-summary-${idx}`}>· {itemSummary(row)}</span>}
        <div className="ml-auto flex items-center gap-1">
          <button type="button" onClick={() => setCollapsed((c) => !c)} data-testid={`rs-row-toggle-${idx}`} className="p-1 rounded hover:bg-slate-100" aria-label="Collapse row">
            <Icons.ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${collapsed ? "" : "rotate-180"}`} />
          </button>
          <button type="button" onClick={onRemove} data-testid={`rs-remove-${idx}`} className="p-1 rounded hover:bg-rose-50 text-rose-500" aria-label="Remove requested service">
            <Icons.Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
      {!collapsed && (
        <div className="p-3 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-medium text-slate-500 mb-1" htmlFor={`rs-service-${idx}`}>Service</label>
              <select id={`rs-service-${idx}`} data-testid={`rs-service-${idx}`} value={row.service_code || ""}
                onChange={(e) => selectService(e.target.value)}
                className="w-full text-sm rounded-lg border border-slate-200 px-2.5 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-200">
                <option value="">Select a service…</option>
                {grouped.map(([cat, svcs]) => (
                  <optgroup key={cat} label={cat}>
                    {svcs.map((s) => <option key={s.service_code} value={s.service_code}>{s.service_name}</option>)}
                  </optgroup>
                ))}
                <option value={options.other_code}>Other (describe)</option>
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-medium text-slate-500 mb-1">Category</label>
              <div data-testid={`rs-category-${idx}`} className="h-[38px] flex items-center">
                {row.category_name
                  ? <span className="inline-flex items-center gap-1 text-xs font-medium rounded-full px-2.5 py-1 bg-slate-100 text-slate-600">{row.category_name}</span>
                  : <span className="text-xs text-slate-300">Derived from service</span>}
              </div>
            </div>
          </div>

          {isOther && (
            <div>
              <label className="block text-[11px] font-medium text-slate-500 mb-1" htmlFor={`rs-custom-${idx}`}>Describe the service</label>
              <input id={`rs-custom-${idx}`} data-testid={`rs-custom-${idx}`} value={row.custom_service_description || ""}
                onChange={(e) => onChange({ ...row, custom_service_description: e.target.value })}
                className="w-full text-sm rounded-lg border border-slate-200 px-2.5 py-2 focus:outline-none focus:ring-2 focus:ring-blue-200"
                placeholder="e.g. Gift tax question" />
            </div>
          )}

          <div>
            <label className="block text-[11px] font-medium text-slate-500 mb-1">Filing year(s)</label>
            <YearControl row={row} bounds={options.year_bounds}
              onYears={(years) => onChange({ ...row, years })}
              onApplicability={(k) => onChange({ ...row, year_applicability: k, years: k === "specific_years" ? row.years : [] })} />
          </div>

          {askApply && (
            <div className="flex items-center gap-2 rounded-lg bg-blue-50 border border-blue-100 px-3 py-2" data-testid={`rs-apply-prior-${idx}`}>
              <Icons.Info className="h-4 w-4 text-blue-500 shrink-0" />
              <span className="text-xs text-blue-800 flex-1">Apply the same filing years as “{priorRow.service_name}” ({spanLabel(priorRow.years)})?</span>
              <button type="button" onClick={() => { onChange({ ...row, year_applicability: "specific_years", years: [...priorRow.years] }); setAskApply(false); }}
                data-testid={`rs-apply-prior-yes-${idx}`} className="text-xs font-semibold rounded-md px-2 py-1 bg-blue-600 text-white hover:bg-blue-700">Apply</button>
              <button type="button" onClick={() => setAskApply(false)} data-testid={`rs-apply-prior-no-${idx}`} className="text-xs rounded-md px-2 py-1 border border-slate-200 hover:bg-white">No</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function RequestedServicesEditor({ items, options, onChange }) {
  const [rows, setRows] = useState([]);
  const lastSig = useRef(null);

  useEffect(() => {
    const s = sig(items);
    if (s !== lastSig.current) {
      lastSig.current = s;
      setRows((items || []).map((i) => ({ ...i, _k: nextKey() })));
    }
  }, [items]);

  const emit = (next) => {
    setRows(next);
    const clean = next.map(({ _k, resolution, candidate_codes, migration_status, ...rest }) => rest);
    lastSig.current = sig(clean);
    onChange(clean);
  };

  const addRow = () => emit([...rows, { _k: nextKey(), service_id: null, service_code: "", service_name: "",
    category_code: null, category_name: null, year_applicability: "specific_years", years: [], source: "staff" }]);
  const updateRow = (i, r) => emit(rows.map((x, idx) => (idx === i ? { ...r, _k: x._k } : x)));
  const removeRow = (i) => emit(rows.filter((_, idx) => idx !== i));

  if (!options) return <div className="text-xs text-slate-400 py-2">Loading services…</div>;

  const needsReview = (r) => (r.migration_status === "review_required")
    || (!r.service_id && r.service_code !== options.other_code && !!r.service_name && r.resolution !== "matched");
  const reviewCount = rows.filter(needsReview).length;

  return (
    <div className="space-y-3" data-testid="requested-services-editor">
      {reviewCount > 0 && (
        <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5" data-testid="rs-review-warning">
          <Icons.AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
          <div className="text-xs text-amber-800">
            <span className="font-semibold">{reviewCount} requested service{reviewCount > 1 ? "s" : ""} need{reviewCount > 1 ? "" : "s"} a specific selection.</span>{" "}
            An imported value couldn’t be matched to an exact Service Library item. Open the row below and choose the precise service to resolve it.
          </div>
        </div>
      )}
      {rows.length === 0 && (
        <div className="text-xs text-slate-400 rounded-lg border border-dashed border-slate-200 px-3 py-4 text-center" data-testid="rs-empty">
          No requested services yet.
        </div>
      )}
      {rows.map((r, i) => (
        <ServiceRow key={r._k} row={r} idx={i} options={options} priorRow={i > 0 ? rows[i - 1] : null}
          review={needsReview(r)} onChange={(nr) => updateRow(i, nr)} onRemove={() => removeRow(i)} />
      ))}
      <button type="button" onClick={addRow} data-testid="rs-add-row"
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:text-blue-700">
        <Icons.Plus className="h-4 w-4" />Add another requested service
      </button>
    </div>
  );
}
